import * as React from "react";
import styled from "styled-components";
import { useContext } from "react";
import { Formik, Form, Field } from "formik";
import { validateField, formHasErrors } from "util/FormsUtils";
import { FormikFormGroup, FormikInput, InputOptionalInfo as Opt } from "components/common";
import { Input, Button, ButtonToolbar } from "components/bootstrap";
import { getPathnameWithoutId } from "util/URLUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import BackendWizardContext from "./BackendWizardContext";
export const STEP_KEY = "server-configuration";
export const FORM_VALIDATION = {
  title: {
    required: true
  },
  serverHost: {
    required: true
  },
  serverPort: {
    required: true,
    min: 1,
    max: 65535
  },
  description: {},
  transportSecurity: {},
  verifyCertificates: {},
  systemUserDn: {},
  systemUserPassword: {}
};
const ServerUrl = styled.div`
  display: flex;

  > * {
    align-self: flex-start;
    min-height: 34px;
    flex-grow: 1;

    &:last-child {
      flex: 0.8;
      min-width: 130px;
    }
  }

  .input-group-addon {
    display: flex;
    align-items: center;
    max-width: fit-content;
    min-width: fit-content;
  }
`;
const ProtocolOptions = styled.div`
  display: flex;

  div + * {
    margin-left: 10px;
  }
`;
const ServerConfigStep = ({ formRef, help = {}, onSubmit, onSubmitAll, submitAllError, validateOnMount }) => {
  const { setStepsState, ...stepsState } = useContext(BackendWizardContext);
  const {
    backendValidationErrors,
    authBackendMeta: { backendHasPassword }
  } = stepsState;
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const _onTransportSecurityChange = (event, values, setFieldValue, onChange) => {
    const currentValue = values.transportSecurity;
    const newValue = event.target.value;
    const defaultPort = 389;
    const defaultTlsPort = 636;
    if (currentValue === "tls" && newValue !== "tls" && values.serverPort === defaultTlsPort) {
      setFieldValue("serverPort", defaultPort);
    }
    if (currentValue !== "tls" && newValue === "tls" && values.serverPort === defaultPort) {
      setFieldValue("serverPort", defaultTlsPort);
    }
    onChange(event);
  };
  const _onSubmitAll = (validateForm) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_SERVER_CONFIG_SAVE_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: "directory-service",
      app_action_value: "server-configuration-save"
    });
    validateForm().then((errors) => {
      if (!formHasErrors(errors)) {
        onSubmitAll();
      }
    });
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: stepsState.formValues,
      innerRef: formRef,
      initialErrors: backendValidationErrors,
      onSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount
    },
    ({ isSubmitting, setFieldValue, values, validateForm }) => /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(FormikFormGroup, { help: help.title, label: "Title", name: "title", placeholder: "Title" }), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.description,
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Description ", /* @__PURE__ */ React.createElement(Opt, null)),
        type: "textarea",
        name: "description",
        placeholder: "Description"
      }
    ), /* @__PURE__ */ React.createElement(Input, { id: "uri-host", label: "Server Address", labelClassName: "col-sm-3", wrapperClassName: "col-sm-9" }, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ServerUrl, { className: "input-group" }, /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        formGroupClassName: "",
        name: "serverHost",
        id: "serverHost",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.serverHost,
        placeholder: "Hostname",
        validate: validateField(FORM_VALIDATION.serverHost)
      }
    ), /* @__PURE__ */ React.createElement("span", { className: "input-group-addon input-group-separator" }, ":"), /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        formGroupClassName: "",
        name: "serverPort",
        id: "serverPort",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.serverPort,
        placeholder: "Port",
        type: "number",
        validate: validateField(FORM_VALIDATION.serverPort)
      }
    )), /* @__PURE__ */ React.createElement(ProtocolOptions, null, /* @__PURE__ */ React.createElement(Field, { name: "transportSecurity" }, ({ field: { name, onChange, onBlur, value } }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        defaultChecked: value === "none",
        formGroupClassName: "",
        id: name,
        label: "None",
        onBlur,
        onChange: (e) => _onTransportSecurityChange(e, values, setFieldValue, onChange),
        type: "radio",
        value: "none"
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        defaultChecked: value === "tls",
        formGroupClassName: "",
        id: name,
        label: "TLS",
        onBlur,
        onChange: (e) => _onTransportSecurityChange(e, values, setFieldValue, onChange),
        type: "radio",
        value: "tls"
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        defaultChecked: value === "start_tls",
        formGroupClassName: "",
        id: name,
        label: "StartTLS",
        onBlur,
        onChange: (e) => _onTransportSecurityChange(e, values, setFieldValue, onChange),
        type: "radio",
        value: "start_tls"
      }
    ))), /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        formGroupClassName: "",
        label: "Verify Certificates",
        name: "verifyCertificates",
        id: "verifyCertificates",
        type: "checkbox"
      }
    )))), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.systemUserDn,
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.systemUserDn,
        label: "System User DN",
        name: "systemUserDn",
        validate: validateField(FORM_VALIDATION.systemUserDn),
        placeholder: "System User DN",
        required: true
      }
    ), backendHasPassword && values.systemUserPassword === void 0 ? /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "systemPassword",
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, "System Password ", /* @__PURE__ */ React.createElement(Opt, null)),
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("systemUserPassword", "") }, "Reset Password")
    ) : /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        autoComplete: "authentication-service-password",
        buttonAfter: backendHasPassword && values.systemUserPassword !== void 0 ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("systemUserPassword", void 0) }, "Undo Reset") : void 0,
        help: help.systemUserPassword,
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, "System Password ", /* @__PURE__ */ React.createElement(Opt, null)),
        name: "systemUserPassword",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.systemUserPassword,
        placeholder: "System Password",
        validate: validateField(FORM_VALIDATION.systemUserPassword),
        type: "password"
      }
    ), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(Button, { disabled: isSubmitting, onClick: () => _onSubmitAll(validateForm), type: "button" }, "Finish & Save Service"), /* @__PURE__ */ React.createElement(
      Button,
      {
        bsStyle: "primary",
        disabled: isSubmitting,
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_NEXT_USER_SYNC_CLICKED, {
            app_pathname: getPathnameWithoutId(pathname),
            app_section: "directory-service",
            app_action_value: "usersync-button"
          });
        },
        type: "submit"
      },
      "Next: User Synchronization"
    )))
  );
};
export default ServerConfigStep;
